/* eslint-disable react/no-unused-prop-types */
import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';
import { BlogSticky } from '@doltech/ui/lib/figma/Blog/BlogSticky';
import { ContentItemGroup } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/ContentItemGroup/ContentItemGroup';
import {
  ContentItemProps,
  TableOfContentProps,
} from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TableOfContentType';
import { TocContentLayout } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TocContentLayout/TocContentLayout';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import * as React from 'react';
import styled from 'styled-components';
import { ContentItemGroupWithEditorGrammarLanding } from './ContentItemGroupWithEditorGrammarLanding';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { IconCourseCourseArrowArrowUp } from '@doltech/icons/Course/IconCourseCourseArrowArrowUp';
import cl from 'classnames';
import { ExpandCollapseAnimation } from '@doltech/ui/lib/examples/Animation/ExpandCollapse';

const Main = styled.div.withConfig({
  componentId: 'TableOfContent_Main',
} as any)``;

interface TableOfContentLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  tocTitle?: string;
  items?: ContentItemProps[];
  editorItems?: any[];
  onItemClick?: () => void;
  scrollContainerElm?: string | Window | HTMLElement;
  currentSectionId?: string;
  onOpenMenu?: () => void;
  expanded?: boolean;
}

/*
 * Default template style
 *
 * */

const DefaultTemplateLayoutWrapper = styled.nav`
  .header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .icon-expand-collapse {
      transition: all 0.3s ease;
      font-size: 24px;
      line-height: 0;
      transform: rotate(180deg);
    }

    &.expanded {
      .icon-expand-collapse {
        transform: rotate(0deg);
      }
    }
  }
`;

const DefaultTemplateLayout = ({ children, onOpenMenu, expanded }: TableOfContentLayoutProps) => (
  <DefaultTemplateLayoutWrapper className="toc-layout aside">
    <header className={cl('header-wrapper', { expanded })} onClick={onOpenMenu}>
      <ResponsiveTypography.Paragraph
        mb="16px"
        ellipsis={{ tooltip: true }}
        variant="semi-bold/16"
        color="black100"
      >
        Nội dung bài viết
      </ResponsiveTypography.Paragraph>
      <div className="icon-expand-collapse">
        <IconCourseCourseArrowArrowUp color={colorsV2.gray100} size={18} />
      </div>
    </header>
    {children}
  </DefaultTemplateLayoutWrapper>
);

const DefaultTemplate = (props: TableOfContentLayoutProps) => {
  const { tocTitle, items, onItemClick, currentSectionId, scrollContainerElm } = props;
  const [expanded, setExpanded] = React.useState(true);

  return (
    <DefaultTemplateLayout
      tocTitle={tocTitle}
      onOpenMenu={() => setExpanded((o) => !o)}
      expanded={expanded}
    >
      <ExpandCollapseAnimation expanded={expanded}>
        <TocContentLayout className="toc-content">
          {items.map((item) => (
            <ContentItemGroup
              key={item.id}
              currentSectionId={currentSectionId}
              onClick={onItemClick}
              scrollContainerElm={scrollContainerElm}
              {...item}
            />
          ))}
        </TocContentLayout>
      </ExpandCollapseAnimation>
    </DefaultTemplateLayout>
  );
};

const DefaultTemplateWithEditor = (props: TableOfContentLayoutProps) => {
  const { tocTitle, editorItems, onItemClick, currentSectionId, scrollContainerElm } = props;
  const [expanded, setExpanded] = React.useState(true);
  return (
    <DefaultTemplateLayout
      tocTitle={tocTitle}
      onOpenMenu={() => setExpanded((o) => !o)}
      expanded={expanded}
    >
      <ExpandCollapseAnimation expanded={expanded}>
        <TocContentLayout className="toc-content">
          {editorItems.map((item, idx) => (
            <ContentItemGroupWithEditorGrammarLanding
              index={idx}
              key={item.id}
              onClick={onItemClick}
              currentSectionId={currentSectionId}
              scrollContainerElm={scrollContainerElm}
              {...item}
            />
          ))}
        </TocContentLayout>
      </ExpandCollapseAnimation>
    </DefaultTemplateLayout>
  );
};

/*
 * Box template style
 *
 * */

const BoxTemplateWrapper = styled.nav`
  background-color: ${colorsV2.white100};
  border: 1px solid ${colorsV2.gray40};
  padding: 16px;
  border-radius: 8px;

  .header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .icon-expand-collapse {
      transition: all 0.3s ease;
      font-size: 24px;
      line-height: 0;
      transform: rotate(180deg);
    }

    &.expanded {
      .icon-expand-collapse {
        transform: rotate(0deg);
      }
    }
  }
`;
const BoxTemplateLayout = ({ children, expanded, onOpenMenu }: TableOfContentLayoutProps) => (
  <BoxTemplateWrapper className="toc-layout box">
    <header className={cl('header-wrapper', { expanded })} onClick={onOpenMenu}>
      <ResponsiveTypography.Paragraph
        mb="16px"
        ellipsis={{ tooltip: true }}
        variant="semi-bold/16"
        color="black100"
      >
        Nội dung bài viết
      </ResponsiveTypography.Paragraph>
      <div className="icon-expand-collapse">
        <IconCourseCourseArrowArrowUp color={colorsV2.gray100} size={18} />
      </div>
    </header>
    {children}
  </BoxTemplateWrapper>
);

const BoxTemplate = (props: TableOfContentLayoutProps) => {
  const { tocTitle, items, onItemClick, currentSectionId, scrollContainerElm } = props;
  const [expanded, setExpanded] = React.useState(true);

  return (
    <BoxTemplateLayout
      tocTitle={tocTitle}
      onOpenMenu={() => setExpanded((o) => !o)}
      expanded={expanded}
    >
      <ExpandCollapseAnimation expanded={expanded}>
        <TocContentLayout className="toc-content">
          {items.map((item, index) => (
            <ContentItemGroup
              key={index}
              currentSectionId={currentSectionId}
              onClick={onItemClick}
              scrollContainerElm={scrollContainerElm}
              {...item}
            />
          ))}
        </TocContentLayout>
      </ExpandCollapseAnimation>
    </BoxTemplateLayout>
  );
};

const BoxTemplateWithEditor = (props: TableOfContentLayoutProps) => {
  const { tocTitle, editorItems, currentSectionId, onItemClick, scrollContainerElm } = props;
  const [expanded, setExpanded] = React.useState(true);
  return (
    <BoxTemplateLayout
      tocTitle={tocTitle}
      onOpenMenu={() => setExpanded((o) => !o)}
      expanded={expanded}
    >
      <ExpandCollapseAnimation expanded={expanded}>
        <TocContentLayout className="toc-content">
          {editorItems.map((item, index) => (
            <ContentItemGroupWithEditorGrammarLanding
              key={item.id}
              currentSectionId={currentSectionId}
              scrollContainerElm={scrollContainerElm}
              onClick={onItemClick}
              {...item}
            />
          ))}
        </TocContentLayout>
      </ExpandCollapseAnimation>
    </BoxTemplateLayout>
  );
};

interface ResponsiveTableOfContentProps extends TableOfContentProps {
  currentSectionId?: string;
}

export const DesktopTableOfContent = (props: ResponsiveTableOfContentProps) => {
  const { title, items, currentSectionId, scrollContainerElm, tocTitle, editorItems, ...rest } =
    props;
  const { appScrollRef } = useAppScrollRef();

  return (
    <Main className="table-of-content">
      <BlogSticky
        key="table-of-content"
        scrollContainerElm={scrollContainerElm || appScrollRef.current}
        {...rest}
      >
        {Boolean(editorItems?.length) && (
          <DefaultTemplateWithEditor
            tocTitle={tocTitle}
            currentSectionId={currentSectionId}
            editorItems={editorItems}
            scrollContainerElm={scrollContainerElm || appScrollRef.current}
          />
        )}
        {Boolean(items?.length) && (
          <DefaultTemplate
            currentSectionId={currentSectionId}
            tocTitle={tocTitle}
            items={items}
            scrollContainerElm={scrollContainerElm || appScrollRef.current}
          />
        )}
      </BlogSticky>
    </Main>
  );
};

export const MobileTableOfContent = (props: ResponsiveTableOfContentProps) => {
  const { title, items, currentSectionId, scrollContainerElm, tocTitle, editorItems } = props;

  return (
    <Main className="table-of-content">
      {Boolean(editorItems?.length) && (
        <BoxTemplateWithEditor
          tocTitle={tocTitle || title}
          editorItems={editorItems}
          currentSectionId={currentSectionId}
          scrollContainerElm={scrollContainerElm}
        />
      )}
      {Boolean(items?.length) && (
        <BoxTemplate
          tocTitle={tocTitle || title}
          items={items}
          currentSectionId={currentSectionId}
          scrollContainerElm={scrollContainerElm}
        />
      )}
    </Main>
  );
};
